import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Download, Copy } from 'lucide-react';
import { logger } from '@/lib/logger';

export default function ExportData() {
  const [exporting, setExporting] = useState(false);
  const [exportData, setExportData] = useState('');
  const { toast } = useToast();

  const generateInsertStatement = (tableName: string, row: any): string => {
    const columns = Object.keys(row);
    const values = columns.map(col => {
      const value = row[col];
      if (value === null || value === undefined) return 'NULL';
      if (typeof value === 'boolean') return value.toString();
      if (typeof value === 'number') return value.toString();
      if (typeof value === 'object') return `'${JSON.stringify(value).replace(/'/g, "''")}'`;
      return `'${String(value).replace(/'/g, "''")}'`;
    });

    return `INSERT INTO public.${tableName} (${columns.join(', ')}) VALUES (${values.join(', ')});`;
  };

  const exportAllData = async () => {
    setExporting(true);
    let allInserts: string[] = [];

    try {
      allInserts.push('-- ========================================');
      allInserts.push('-- Data Export from North Reports');
      allInserts.push(`-- Generated: ${new Date().toISOString()}`);
      allInserts.push('-- ========================================');
      allInserts.push('');

      // 1. Export Teams
      allInserts.push('-- Teams');
      const { data: teams, error: teamsError } = await supabase
        .from('teams')
        .select('*');

      if (teamsError) throw teamsError;
      if (teams) {
        teams.forEach(team => {
          allInserts.push(generateInsertStatement('teams', team));
        });
      }
      allInserts.push('');

      // 2. Export Profiles
      allInserts.push('-- Profiles');
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*');

      if (profilesError) throw profilesError;
      if (profiles) {
        profiles.forEach(profile => {
          allInserts.push(generateInsertStatement('profiles', profile));
        });
      }
      allInserts.push('');

      // 3. Export User Roles
      allInserts.push('-- User Roles');
      const { data: userRoles, error: userRolesError } = await supabase
        .from('user_roles')
        .select('*');

      if (userRolesError) throw userRolesError;
      if (userRoles) {
        userRoles.forEach(role => {
          allInserts.push(generateInsertStatement('user_roles', role));
        });
      }
      allInserts.push('');

      // 4. Export Norths
      allInserts.push('-- Norths (North Reports)');
      const { data: norths, error: northsError } = await supabase
        .from('norths')
        .select('*');

      if (northsError) throw northsError;
      if (norths) {
        norths.forEach(north => {
          allInserts.push(generateInsertStatement('norths', north));
        });
      }
      allInserts.push('');

      // 5. Export Comparable Properties
      allInserts.push('-- Comparable Properties');
      const { data: comps, error: compsError } = await supabase
        .from('comparable_properties')
        .select('*');

      if (compsError) throw compsError;
      if (comps) {
        comps.forEach(comp => {
          allInserts.push(generateInsertStatement('comparable_properties', comp));
        });
      }
      allInserts.push('');

      // 6. Export Showing Events
      allInserts.push('-- Showing Events');
      const { data: showings, error: showingsError } = await supabase
        .from('showing_events')
        .select('*');

      if (showingsError) throw showingsError;
      if (showings) {
        showings.forEach(showing => {
          allInserts.push(generateInsertStatement('showing_events', showing));
        });
      }
      allInserts.push('');

      // 7. Export Agent Insights
      allInserts.push('-- Agent Insights');
      const { data: insights, error: insightsError } = await supabase
        .from('agent_insights')
        .select('*');

      if (insightsError) throw insightsError;
      if (insights) {
        insights.forEach(insight => {
          allInserts.push(generateInsertStatement('agent_insights', insight));
        });
      }
      allInserts.push('');

      // 8. Export Social Media Accounts
      allInserts.push('-- Social Media Accounts');
      const { data: socialAccounts, error: socialAccountsError } = await supabase
        .from('social_media_accounts')
        .select('*');

      if (socialAccountsError) throw socialAccountsError;
      if (socialAccounts) {
        socialAccounts.forEach(account => {
          allInserts.push(generateInsertStatement('social_media_accounts', account));
        });
      }
      allInserts.push('');

      // 9. Export Social Media Posts
      allInserts.push('-- Social Media Posts');
      const { data: posts, error: postsError } = await supabase
        .from('social_media_posts')
        .select('*');

      if (postsError) throw postsError;
      if (posts) {
        posts.forEach(post => {
          allInserts.push(generateInsertStatement('social_media_posts', post));
        });
      }
      allInserts.push('');

      // 10. Export Team Subscriptions
      allInserts.push('-- Team Subscriptions');
      const { data: subscriptions, error: subscriptionsError } = await supabase
        .from('team_subscriptions')
        .select('*');

      if (subscriptionsError) throw subscriptionsError;
      if (subscriptions) {
        subscriptions.forEach(subscription => {
          allInserts.push(generateInsertStatement('team_subscriptions', subscription));
        });
      }
      allInserts.push('');

      // 11. Export Team Invitations
      allInserts.push('-- Team Invitations');
      const { data: invitations, error: invitationsError } = await supabase
        .from('team_invitations')
        .select('*');

      if (invitationsError) throw invitationsError;
      if (invitations) {
        invitations.forEach(invitation => {
          allInserts.push(generateInsertStatement('team_invitations', invitation));
        });
      }
      allInserts.push('');

      // Summary
      allInserts.push('-- ========================================');
      allInserts.push('-- Export Summary');
      allInserts.push(`-- Teams: ${teams?.length || 0}`);
      allInserts.push(`-- Profiles: ${profiles?.length || 0}`);
      allInserts.push(`-- User Roles: ${userRoles?.length || 0}`);
      allInserts.push(`-- Norths: ${norths?.length || 0}`);
      allInserts.push(`-- Comparable Properties: ${comps?.length || 0}`);
      allInserts.push(`-- Showing Events: ${showings?.length || 0}`);
      allInserts.push(`-- Agent Insights: ${insights?.length || 0}`);
      allInserts.push(`-- Social Media Accounts: ${socialAccounts?.length || 0}`);
      allInserts.push(`-- Social Media Posts: ${posts?.length || 0}`);
      allInserts.push(`-- Team Subscriptions: ${subscriptions?.length || 0}`);
      allInserts.push(`-- Team Invitations: ${invitations?.length || 0}`);
      allInserts.push('-- ========================================');

      const finalSQL = allInserts.join('\n');
      setExportData(finalSQL);

      toast({
        title: "Export Complete",
        description: `Exported ${norths?.length || 0} North Reports and all related data`,
      });

    } catch (error: any) {
      logger.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(exportData);
    toast({
      title: "Copied!",
      description: "SQL export copied to clipboard",
    });
  };

  const downloadFile = () => {
    const blob = new Blob([exportData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `north-export-${new Date().toISOString().split('T')[0]}.sql`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Downloaded!",
      description: "SQL export file saved",
    });
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      <Card>
        <CardHeader>
          <CardTitle>Export Data from North Reports</CardTitle>
          <CardDescription>
            Generate SQL INSERT statements for all your data to migrate to new Supabase instance
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <Button
              onClick={exportAllData}
              disabled={exporting}
              className="flex-1"
            >
              {exporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting Data...
                </>
              ) : (
                'Generate Export SQL'
              )}
            </Button>
          </div>

          {exportData && (
            <>
              <div className="flex gap-2">
                <Button
                  onClick={copyToClipboard}
                  variant="outline"
                  size="sm"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy to Clipboard
                </Button>
                <Button
                  onClick={downloadFile}
                  variant="outline"
                  size="sm"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download SQL File
                </Button>
              </div>

              <Textarea
                value={exportData}
                readOnly
                className="font-mono text-xs h-[500px]"
                placeholder="Generated SQL will appear here..."
              />

              <div className="text-sm text-muted-foreground">
                <p className="font-semibold">Next Steps:</p>
                <ol className="list-decimal list-inside space-y-1 mt-2">
                  <li>Download or copy the SQL above</li>
                  <li>Save it as <code className="bg-muted px-1 py-0.5 rounded">north-export.sql</code></li>
                  <li>Place it in <code className="bg-muted px-1 py-0.5 rounded">migration-scripts/data-export/</code></li>
                  <li>Run the import script on your local machine connected to new Supabase</li>
                </ol>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
